"use client"
import { useState, useEffect, useRef } from "react"
import "./NearSolutions.css"

const numbers = [
  { num: "+20", text: "Industry sectors" },
  { num: "78", text: "Customer NPS score" },
  { num: "99%", text: "Recommendation rate" },
  { num: "+300", text: "Projects delivered" },
]

const NearSolutions = () => {
  const [active, setActive] = useState("solutions") // State for this navbar's active link
  const headingRef = useRef(null)
  const paraRef = useRef(null)
  const descRef = useRef(null)
  const numbersRef = useRef(null)

  // Define section IDs for the near-solutions-navbar highlighting based on actual section IDs in this component
  // Currently, only the main section has an ID. If you add more sections with IDs, include them here.
  const sectionsToHighlight = [
    "solutions",
    "process",
    "benefits",
    "team",
    "tech-stack",
    "faq",
    "contact-us" // This is the ID of the main section
    // Add other section IDs here if they exist within NearSolutions.jsx
    // e.g., "process", "benefits", etc.
  ]

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.1 }, // Lower threshold to trigger earlier
    )
    // Observe all elements including number blocks
    const elementsToObserve = [headingRef.current, paraRef.current, descRef.current]
    // Add number blocks to observation
    const numberBlocks = document.querySelectorAll(".number-block")
    numberBlocks.forEach((block) => elementsToObserve.push(block))
    elementsToObserve.forEach((ref) => {
      if (ref) observer.observe(ref)
    })

    return () => observer.disconnect()
  }, [])

  // Effect for the near-solutions-navbar highlighting
  useEffect(() => {
    const handleScroll = () => {
      // Approximate height of this near-solutions-navbar
      // You might need to adjust this value based on the actual height of your navbar
      const nearSolutionsNavbarHeight = 60
      const scrollPosition = window.scrollY + nearSolutionsNavbarHeight + 20 // Add a small buffer

      for (let i = sectionsToHighlight.length - 1; i >= 0; i--) {
        const section = document.getElementById(sectionsToHighlight[i])
        if (section && section.offsetTop <= scrollPosition) {
          // The section ID "solutions" directly matches the navbar link "solutions"
          setActive(sectionsToHighlight[i])
          break
        }
      }
    }

    window.addEventListener("scroll", handleScroll)
    handleScroll() // Call once to set initial state

    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const handleClick = (id) => {
    setActive(id) // Set active state to the clicked navbar item text
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  return (
    <>
     <div className="near-solutions-navbar">
        <ul>
          {["solutions", "process", "benefits", "team", "tech stack", "faq", "contact us"].map((item) => (
            <li key={item}>
              <a onClick={() => handleClick(item)} className={active === item ? "active" : ""}>
                {item.toUpperCase()}
              </a>
            </li>
          ))}
        </ul>
      </div>
    <section className="near-solutions-section" id="solutions">
      <div className="container">
     
      <div className="content">
        <div className="numbers-row" ref={numbersRef}>
          {numbers.map((n, index) => (
            <div
              key={n.num}
              className="number-block fade-up visible"
              style={{ "--animation-delay": `${index * 0.1}s` }}
            >
              <div className="big-num">{n.num}</div>
              <div className="num-text">{n.text}</div>
            </div>
          ))}
        </div>
        <div className="blue-divider"></div>
        <div className="near-solutions-header">
          <div className="header-left">
            <h2 ref={headingRef} className="fade-in-left">
              OUR <br />
              <span className="blue">SOLUTIONS</span>
            </h2>
            <p className="near-solutions-description fade-in-left" ref={descRef}>
              Our teams offer technical and vertical expertise, cultural alignment, cost-effectiveness, and data
              security. You will benefit from our agility, innovation, rapid prototyping, and scalable solutions to
              support your growth and enhance your competitive advantage.
            </p>
          </div>
          <div className="header-right">
            <p className="near-solutions-intro fade-in-right" ref={paraRef}>
              Our EU-based <strong>Nearshore Teams</strong> are dedicated to helping your company with a fast
              development, communication, and delivery quality.
            </p>
          </div>
        </div>
      </div>
      </div>
    </section>
    </>
  )
}

export default NearSolutions
